import { useState, useRef, useCallback, useEffect } from "react";
import {
  Volume2,
  Lock,
  Play,
  Pause,
  RotateCcw,
  User,
  AlertCircle,
  Square,
  Zap,
  WifiOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import type { SolutionStep } from "./SolutionView";
import { getLanguageByCode } from "@/lib/languages";
import { idbGetAudio, idbPutAudio, hashText } from "@/lib/audio-cache";

interface VoiceExplanationProps {
  isPaid: boolean;
  steps: SolutionStep[];
  finalAnswer: string;
  concept: string;
  language?: string;
  onActiveStepChange?: (stepIndex: number | null) => void;
}

type Speed = 0.75 | 1 | 1.25;

const SETTINGS_KEY = "knowli.narration.settings.v1";

interface StoredNarrationSettings {
  voiceId?: string;
  speed?: Speed;
}

function loadStoredSettings(): StoredNarrationSettings {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    return raw ? (JSON.parse(raw) as StoredNarrationSettings) : {};
  } catch {
    return {};
  }
}

function saveStoredSettings(next: StoredNarrationSettings) {
  if (typeof window === "undefined") return;
  try {
    const current = loadStoredSettings();
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify({ ...current, ...next }));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

interface VoiceOption {
  id: string;
  label: string;
  voiceId: string;
  emoji: string;
}

interface NarrationSegment {
  text: string;
  stepIndex: number | null; // null = intro or final answer
  charStart: number;
  charEnd: number;
}

function buildNarrationWithSegments(
  steps: SolutionStep[],
  finalAnswer: string,
  concept: string
): { fullText: string; segments: NarrationSegment[] } {
  const segments: NarrationSegment[] = [];
  let offset = 0;

  const intro = `Let's solve this step by step using ${concept}.\n\n`;
  segments.push({ text: intro, stepIndex: null, charStart: offset, charEnd: offset + intro.length });
  offset += intro.length;

  steps.forEach((step, i) => {
    let stepText = `Step ${i + 1}: ${step.title}.\n${step.explanation}\n`;
    if (step.work) {
      stepText += `The work here is: ${step.work}.\n`;
    }
    stepText += "\n";
    segments.push({ text: stepText, stepIndex: i, charStart: offset, charEnd: offset + stepText.length });
    offset += stepText.length;
  });

  const outro = `Final answer: ${finalAnswer}.`;
  segments.push({ text: outro, stepIndex: null, charStart: offset, charEnd: offset + outro.length });
  offset += outro.length;

  const fullText = segments.map((s) => s.text).join("");
  return { fullText, segments };
}

export function VoiceExplanation({
  isPaid,
  steps,
  finalAnswer,
  concept,
  language = "en",
  onActiveStepChange,
}: VoiceExplanationProps) {
  const langConfig = getLanguageByCode(language);
  const voices = langConfig.ttsVoices;
  const stored = loadStoredSettings();
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(
    stored.speed && [0.75, 1, 1.25].includes(stored.speed) ? stored.speed : 1,
  );
  const [selectedVoice, setSelectedVoice] = useState<VoiceOption>(
    voices.find((v) => v.voiceId === stored.voiceId) ?? voices[0],
  );
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  const [error, setError] = useState<{
    message: string;
    chunkIndex: number;
    total: number;
  } | null>(null);
  const [hasAudio, setHasAudio] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [preGenerate, setPreGenerate] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const segmentsRef = useRef<NarrationSegment[]>([]);
  const highlightTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastVoiceIdRef = useRef<string>(selectedVoice.voiceId);
  // In-memory cache: key = `${voiceId}|${language}|${speed}|${hash(text)}` → MP3 Blob.
  // Mirrored into IndexedDB for cross-reload persistence.
  const audioCacheRef = useRef<Map<string, Blob>>(new Map());
  const partsRef = useRef<Blob[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const retryButtonRef = useRef<HTMLButtonElement | null>(null);
  const playButtonRef = useRef<HTMLButtonElement | null>(null);

  // Persist narration preferences.
  useEffect(() => {
    saveStoredSettings({ voiceId: selectedVoice.voiceId });
  }, [selectedVoice.voiceId]);
  useEffect(() => {
    saveStoredSettings({ speed });
  }, [speed]);

  // Track online/offline for offline-mode messaging.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const speedMap: Record<Speed, number> = { 0.75: 0.85, 1: 1.0, 1.25: 1.15 };

  // Estimate which step is being spoken based on audio progress
  const startHighlightTracking = useCallback(() => {
    if (highlightTimerRef.current) clearInterval(highlightTimerRef.current);

    highlightTimerRef.current = setInterval(() => {
      const audio = audioRef.current;
      if (!audio || audio.paused || !audio.duration) return;

      const progress = audio.currentTime / audio.duration;
      const segments = segmentsRef.current;
      if (!segments.length) return;

      const totalChars = segments[segments.length - 1].charEnd;
      const currentChar = progress * totalChars;

      const activeSegment = segments.find(
        (s) => currentChar >= s.charStart && currentChar < s.charEnd
      );

      onActiveStepChange?.(activeSegment?.stepIndex ?? null);
    }, 200);
  }, [onActiveStepChange]);

  const stopHighlightTracking = useCallback(() => {
    if (highlightTimerRef.current) {
      clearInterval(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    onActiveStepChange?.(null);
  }, [onActiveStepChange]);

  useEffect(() => {
    return () => {
      stopHighlightTracking();
      // Hard stop on unmount so tutor voice never keeps playing after
      // the user navigates to a different page.
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        } catch {
          /* ignore */
        }
        audioRef.current = null;
      }
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    };
  }, [stopHighlightTracking]);

  const clearAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setHasAudio(false);
    setIsPlaying(false);
    setProgress(null);
    stopHighlightTracking();
  }, [stopHighlightTracking]);

  // Reset voice selection when language changes
  useEffect(() => {
    const newVoices = getLanguageByCode(language).ttsVoices;
    setSelectedVoice(newVoices[0]);
    audioCacheRef.current.clear();
    clearAudio();
  }, [language, clearAudio]);

  const fetchSegmentAudio = useCallback(
    async (
      segmentText: string,
      voiceId: string,
      mappedSpeed: number,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const cacheKey = `${voiceId}|${language}|${mappedSpeed}|${hashText(segmentText)}`;
      const cached = audioCacheRef.current.get(cacheKey);
      if (cached) return cached;
      const persisted = await idbGetAudio(cacheKey);
      if (persisted) {
        audioCacheRef.current.set(cacheKey, persisted);
        return persisted;
      }

      // Offline: no network generation possible. Surface a clear message
      // instead of firing a request that will fail opaquely.
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        throw new Error(
          "You're offline — this narration hasn't been cached yet, so it can't be generated right now.",
        );
      }

      const { supabase } = await import("@/integrations/supabase/client");
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/text-to-speech`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
          body: JSON.stringify({
            text: segmentText,
            speed: mappedSpeed,
            voiceId,
            language,
          }),
          signal,
        }
      );
      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const data = await response.json();
          detail = data?.error || detail;
        } catch {
          const t = await response.text().catch(() => "");
          if (t) detail = t;
        }
        throw new Error(detail);
      }
      const blob = await response.blob();
      audioCacheRef.current.set(cacheKey, blob);
      void idbPutAudio(cacheKey, blob);
      return blob;
    },
    [language]
  );

  // Generate segments starting at `startIndex`, appending into partsRef.
  // Used both by initial play and by Retry (resume from failed chunk).
  const generateFrom = useCallback(
    async (startIndex: number, autoPlay: boolean) => {
      const mappedSpeed = speedMap[speed];
      const { segments } = buildNarrationWithSegments(steps, finalAnswer, concept);
      segmentsRef.current = segments;
      if (startIndex === 0) partsRef.current = [];

      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);
      setProgress({ current: startIndex, total: segments.length });

      try {
        for (let i = startIndex; i < segments.length; i++) {
          const seg = segments[i];
          try {
            const blob = await fetchSegmentAudio(
              seg.text,
              selectedVoice.voiceId,
              mappedSpeed,
              controller.signal,
            );
            partsRef.current[i] = blob;
            setProgress({ current: i + 1, total: segments.length });
          } catch (err) {
            if (controller.signal.aborted) return;
            setError({
              message: err instanceof Error ? err.message : "Failed to generate audio",
              chunkIndex: i,
              total: segments.length,
            });
            return;
          }
        }

        const merged = new Blob(partsRef.current, { type: "audio/mpeg" });
        if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
        const url = URL.createObjectURL(merged);
        audioUrlRef.current = url;
        lastVoiceIdRef.current = selectedVoice.voiceId;

        const audio = new Audio(url);
        audioRef.current = audio;
        audio.playbackRate = speed;

        audio.addEventListener("ended", () => {
          setIsPlaying(false);
          stopHighlightTracking();
        });
        audio.addEventListener("error", () => {
          const mediaErr = audio.error;
          setError({
            message: `Playback error${mediaErr ? ` (code ${mediaErr.code}): ${mediaErr.message || "media element failed"}` : ""}`,
            chunkIndex: -1,
            total: segmentsRef.current.length,
          });
          setIsPlaying(false);
          stopHighlightTracking();
        });

        setHasAudio(true);
        if (autoPlay) {
          await audio.play();
          setIsPlaying(true);
          startHighlightTracking();
        }
      } finally {
        setIsLoading(false);
        setProgress(null);
        abortRef.current = null;
      }
    },
    [
      steps,
      finalAnswer,
      concept,
      speed,
      selectedVoice,
      fetchSegmentAudio,
      startHighlightTracking,
      stopHighlightTracking,
    ],
  );

  const generateAndPlay = useCallback(async () => {
    setError(null);
    if (audioRef.current && audioUrlRef.current && lastVoiceIdRef.current === selectedVoice.voiceId) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
      startHighlightTracking();
      return;
    }
    clearAudio();
    await generateFrom(0, true);
  }, [selectedVoice, clearAudio, generateFrom, startHighlightTracking]);

  // Pre-generate all segments as soon as the explanation is shown so hitting
  // Play begins instantly. Silently skipped when disabled or already cached.
  useEffect(() => {
    if (!isPaid || !preGenerate) return;
    if (audioUrlRef.current && lastVoiceIdRef.current === selectedVoice.voiceId) return;
    if (isLoading || abortRef.current) return;
    void generateFrom(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaid, preGenerate, selectedVoice.voiceId, language, speed, steps, finalAnswer, concept]);

  const togglePlayPause = useCallback(() => {
    if (!audioRef.current || !audioUrlRef.current) {
      generateAndPlay();
      return;
    }
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      stopHighlightTracking();
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      startHighlightTracking();
    }
  }, [isPlaying, generateAndPlay, startHighlightTracking, stopHighlightTracking]);

  const handleReplay = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
      startHighlightTracking();
    }
  }, [startHighlightTracking]);

  const handleSpeedChange = useCallback(() => {
    const speeds: Speed[] = [0.75, 1, 1.25];
    const nextIndex = (speeds.indexOf(speed) + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];
    setSpeed(newSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
  }, [speed]);

  const handleVoiceSelect = useCallback((voice: VoiceOption) => {
    if (voice.id !== selectedVoice.id) {
      clearAudio();
      setSelectedVoice(voice);
    }
    setShowVoiceMenu(false);
  }, [selectedVoice, clearAudio]);

  const handleRetry = useCallback(() => {
    const resumeFrom = error?.chunkIndex ?? 0;
    setError(null);
    // -1 = playback failure; regenerate everything.
    void generateFrom(resumeFrom >= 0 ? resumeFrom : 0, true);
  }, [error, generateFrom]);

  // Focus the retry button when a new error appears so screen-reader / keyboard
  // users are dropped straight onto the actionable control.
  useEffect(() => {
    if (error) retryButtonRef.current?.focus();
  }, [error]);

  // Hard stop: aborts in-flight generation AND halts audio.
  const handleStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setIsLoading(false);
    setProgress(null);
    stopHighlightTracking();
  }, [stopHighlightTracking]);

  // Keyboard shortcuts: P = play/pause, S = stop. Ignored while typing.
  useEffect(() => {
    if (!isPaid) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        target?.isContentEditable ||
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT"
      ) {
        return;
      }
      const k = e.key.toLowerCase();
      if (k === "p") {
        e.preventDefault();
        togglePlayPause();
      } else if (k === "s") {
        e.preventDefault();
        handleStop();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPaid, togglePlayPause, handleStop]);

  // Free user locked state
  if (!isPaid) {
    return (
      <Link to="/upgrade">
        <div className="flex items-center gap-3 bg-muted/50 rounded-xl px-4 py-3 border border-rule cursor-pointer hover:border-neon-pink/40 transition-colors">
          <Lock className="size-4 text-ink-muted shrink-0" />
          <span className="text-sm text-ink-muted font-medium">
            🔒 Upgrade to hear step-by-step explanations
          </span>
        </div>
      </Link>
    );
  }

  return (
    <div className="space-y-2">
      {!isOnline && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400"
        >
          <WifiOff className="size-4 shrink-0" aria-hidden="true" />
          <span>
            Offline mode — only previously cached narration is available. New
            audio can't be generated until you're back online.
          </span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Play / Pause */}
        <Button
          ref={playButtonRef}
          variant="subtle"
          size="sm"
          onClick={togglePlayPause}
          disabled={isLoading}
          className="rounded-xl gap-2"
          aria-keyshortcuts="P"
        >
          {isLoading ? (
            <>
              <Volume2 className="size-4 animate-pulse" />
              Generating...
            </>
          ) : isPlaying ? (
            <>
              <Pause className="size-4" />
              Pause
            </>
          ) : (
            <>
              <Play className="size-4" />
              Play Explanation
            </>
          )}
        </Button>

        {/* Stop / Cancel (visible while generating OR playing) */}
        {(isLoading || isPlaying) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleStop}
            aria-label="Stop narration (S)"
            aria-keyshortcuts="S"
            className="rounded-xl gap-1.5 text-xs"
          >
            <Square className="size-3.5" />
            Stop
          </Button>
        )}

        {/* Voice selector */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowVoiceMenu(!showVoiceMenu)}
            className="rounded-xl gap-1.5 text-xs"
            aria-haspopup="menu"
            aria-expanded={showVoiceMenu}
            aria-label={`Change narration voice (current: ${selectedVoice.label})`}
          >
            <User className="size-3.5" />
            {selectedVoice.emoji} {selectedVoice.label}
          </Button>

          {showVoiceMenu && (
            <div
              role="menu"
              aria-label="Narration voice"
              className="absolute top-full left-0 mt-1 bg-card rounded-xl border border-rule shadow-lg z-20 min-w-[140px] overflow-hidden"
            >
              {voices.map((voice: VoiceOption) => (
                <button
                  key={voice.id}
                  role="menuitemradio"
                  aria-checked={voice.id === selectedVoice.id}
                  onClick={() => handleVoiceSelect(voice)}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-muted/50 transition-colors ${
                    voice.id === selectedVoice.id ? "bg-muted/30 font-semibold text-ink" : "text-ink-muted"
                  }`}
                >
                  <span>{voice.emoji}</span>
                  <span>{voice.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Replay + Speed (show when audio exists) */}
        {hasAudio && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReplay}
              className="rounded-xl gap-1 text-xs"
              aria-label="Replay narration from the beginning"
            >
              <RotateCcw className="size-3.5" />
              Replay
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleSpeedChange}
              className="rounded-xl text-xs font-mono tabular-nums min-w-[60px]"
              aria-label={`Playback speed ${speed}x, click to change`}
            >
              {speed}x
            </Button>
          </>
        )}

        <label className="ml-auto flex items-center gap-1.5 text-xs text-ink-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={preGenerate}
            onChange={(e) => setPreGenerate(e.target.checked)}
            className="accent-neon-pink"
            aria-label="Pre-generate narration for all steps"
          />
          <Zap className="size-3.5" />
          Pre-generate all steps
        </label>
      </div>

      {isLoading && progress && progress.total > 0 && (
        <div className="space-y-1">
          <div
            className="flex items-center justify-between text-xs text-ink-muted"
            data-testid="tts-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.current}
            aria-label="Narration generation progress"
          >
            <span>
              Generating narration… {progress.current}/{progress.total}
            </span>
            <span className="tabular-nums">
              {Math.round((progress.current / progress.total) * 100)}%
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-neon-pink transition-all duration-300"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {error && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          <AlertCircle className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold">
              {error.chunkIndex >= 0
                ? `Narration failed on chunk ${error.chunkIndex + 1}/${error.total}`
                : "Voice playback failed"}
            </div>
            <div className="opacity-90 break-words">{error.message}</div>
          </div>
          <Button
            ref={retryButtonRef}
            variant="ghost"
            size="sm"
            onClick={handleRetry}
            className="h-7 rounded-lg text-xs text-destructive hover:bg-destructive/20"
          >
            <RotateCcw className="size-3.5 mr-1" />
            {error.chunkIndex >= 0 ? `Retry chunk ${error.chunkIndex + 1}` : "Retry"}
          </Button>
        </div>
      )}
    </div>
  );
}
